import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ExternalLink, Scale } from "lucide-react";

const PATH_TO_TYPE = {
  termos: "terms",
  privacidade: "privacy",
  cookies: "cookies",
  terms: "terms",
  privacy: "privacy",
  reclamacoes: "complaints",
  complaints: "complaints",
};

const TITLES = {
  terms: "Termos & Condições",
  privacy: "Política de Privacidade",
  cookies: "Política de Cookies",
  complaints: "Livro de Reclamações",
};

const LR_URL = "https://www.livroreclamacoes.pt/";

export default function LegalView() {
  const { pagePath } = useParams();
  const location = useLocation();
  const [params] = useSearchParams();
  const lang = params.get("lang") || "PT";
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  const derived = pagePath || location.pathname.replace(/^\/+|\/+$/g, "").split("/").pop();
  const type = PATH_TO_TYPE[derived] || derived;

  useEffect(() => {
    api.get(`/public/legal/${type}?lang=${lang}`)
      .then((r) => { setDoc(r.data); setErr(""); })
      .catch(() => setErr("Esta página ainda não foi publicada."));
  }, [type, lang]);

  const isComplaints = type === "complaints";

  return (
    <div className="min-h-screen bg-white text-slate-800 py-16 px-6" data-testid={`legal-view-${type}`}>
      <div className="max-w-3xl mx-auto">
        <a href="/" className="text-xs uppercase tracking-widest text-[#f59e0b] hover:underline">← DiTours</a>

        <div className="mt-6 mb-10">
          <p className="text-[10px] uppercase tracking-widest text-[#f59e0b]">DiTours · {lang}</p>
          <h1 className="font-display text-4xl text-[#0a0f1e] mt-2">{TITLES[type] || "Legal"}</h1>
          {doc && (
            <div className="text-xs text-slate-500 font-mono mt-3">v{doc.version} · em vigor desde {doc.effective_date}</div>
          )}
        </div>

        {isComplaints && (
          <div className="border-2 border-[#f59e0b] bg-amber-50/40 p-6 mb-10" data-testid="livro-reclamacoes-cta">
            <div className="flex items-start gap-3">
              <Scale className="w-6 h-6 text-[#f59e0b] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display text-xl text-[#0a0f1e] mb-2">Livro de Reclamações Electrónico</div>
                <p className="text-sm text-slate-700 mb-4 leading-relaxed">
                  Nos termos do <strong>Decreto-Lei n.º 74/2017</strong>, a DiTours Portugal dispõe de Livro de Reclamações
                  em formato electrónico. Pode apresentar a sua reclamação directamente no portal oficial do Governo,
                  onde será entrega à entidade reguladora competente (Instituto da Mobilidade e dos Transportes — IMT).
                </p>
                <a
                  href={LR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="livro-reclamacoes-link"
                  className="inline-flex items-center gap-2 h-11 px-6 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide hover:brightness-110"
                >
                  Abrir Livro de Reclamações <ExternalLink className="w-4 h-4" />
                </a>
                <div className="mt-4 text-[11px] text-slate-600 font-mono break-all">
                  URL directo: <a href={LR_URL} target="_blank" rel="noopener noreferrer" className="text-[#f59e0b] hover:underline">{LR_URL}</a>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#f59e0b]/30">
              <div className="label-caps mb-3">Contactos DiTours</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Email</div>
                  <a href="mailto:reclamacoes@ditours.pt" className="text-[#0a0f1e] hover:text-[#f59e0b]">reclamacoes@ditours.pt</a>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Telefone / WhatsApp</div>
                  <a href="https://wa.me/351915498051" className="text-[#0a0f1e] hover:text-[#f59e0b]">+351 915 498 051</a>
                </div>
              </div>
            </div>

            <div className="mt-4 text-[11px] text-slate-500">
              Para além do Livro de Reclamações, pode também recorrer a arbitragem de consumo através de{" "}
              <a href="https://www.consumidor.gov.pt/pt/rncc/pesquisa-de-entidades" target="_blank" rel="noopener noreferrer" className="text-[#f59e0b] hover:underline">
                entidades reconhecidas pela DGC
              </a>.
            </div>
          </div>
        )}

        {err && !isComplaints && (
          <div className="mt-12 border border-slate-200 p-8 text-center text-slate-500">{err}</div>
        )}
        {doc && (
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{doc.content}</pre>
        )}
      </div>
    </div>
  );
}
